/*
  Smart filters: Category, Color and Back Height, with no app setup.

  Shopify only returns filters that are switched on in the Search & Discovery
  app (by default just Availability and Price). This script builds the three
  filters the store needs from the collection's own product data, read from
  the public /collections/<handle>/products.json endpoint:

    Category     product type ("Executive Chairs", "Visitor Chairs"…)
    Color        a "Color"/"Colour" variant option; else tags such as
                 "Color: Black" / "color_black"; else a colour word in the
                 product title
    Back Height  a "Back Height" option or tag; else "High Back" / "Mid Back"
                 / "Low Back" in the product title (e.g. "High-Back Office Chair")

  If Search & Discovery later sends a real Category / Color / Back Height
  filter, the matching group here switches itself off and Shopify's version
  is used instead (see data-native-* on the mount).

  When a value is ticked, every page of the collection's grid (with any
  native filters such as Price still applied) is loaded through the Section
  Rendering API and only matching cards are shown, so results aren't limited
  to the current page. Choices are kept in the URL (sf_category, sf_color,
  sf_back) so filtered links can be shared and survive a reload.
*/
(function () {
  'use strict';

  var PARAM = { category: 'sf_category', color: 'sf_color', back: 'sf_back' };
  var LABEL = { category: 'Category', color: 'Color', back: 'Back Height' };
  var ORDER = ['category', 'color', 'back'];
  var MAX_PAGES = 25;

  var COLOR_WORDS = [
    'black', 'white', 'grey', 'gray', 'silver', 'charcoal', 'blue', 'navy',
    'red', 'maroon', 'burgundy', 'brown', 'tan', 'beige', 'cream', 'ivory',
    'green', 'olive', 'yellow', 'orange', 'pink', 'purple', 'gold',
    'walnut', 'oak', 'mahogany', 'cherry', 'wenge', 'teak', 'espresso'
  ];
  var CSS_COLOR = {
    walnut: '#5d432c', oak: '#c8a165', mahogany: '#6b2d1f', cherry: '#8e3b2c',
    wenge: '#3b2a20', teak: '#a8753b', espresso: '#3c2a21', charcoal: '#36454f',
    cream: '#f3ead3', tan: '#d2b48c', gray: '#808080'
  };

  var productIndexPromise = null;
  var state = { category: [], color: [], back: [] };
  var gridCache = {};

  function titleCase(s) {
    return String(s).trim().toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function uniq(list) {
    var seen = {};
    return list.filter(function (v) {
      var k = v.toLowerCase();
      if (!v || seen[k]) return false;
      seen[k] = true;
      return true;
    });
  }

  // ---------- product attributes ----------
  function colorsFor(p) {
    var out = [];
    (p.options || []).forEach(function (o) {
      if (/^colou?r$/i.test(o.name) || /colou?r/i.test(o.name)) {
        (o.values || []).forEach(function (v) { out.push(titleCase(v)); });
      }
    });
    if (out.length) return uniq(out);
    (p.tags || []).forEach(function (t) {
      var m = /^colou?r[\s:_-]+(.+)$/i.exec(t);
      if (m) out.push(titleCase(m[1]));
    });
    if (out.length) return uniq(out);
    var title = ' ' + String(p.title || '').toLowerCase().replace(/[^a-z]+/g, ' ') + ' ';
    COLOR_WORDS.forEach(function (w) {
      if (title.indexOf(' ' + w + ' ') !== -1) out.push(titleCase(w === 'gray' ? 'grey' : w));
    });
    return uniq(out);
  }

  function backFor(p) {
    var out = [];
    (p.options || []).forEach(function (o) {
      if (/back/i.test(o.name)) (o.values || []).forEach(function (v) { out.push(titleCase(v)); });
    });
    if (out.length) return uniq(out);
    (p.tags || []).forEach(function (t) {
      var m = /^back[\s_-]*height[\s:_-]+(.+)$/i.exec(t);
      if (m) out.push(titleCase(m[1].replace(/-/g, ' ')));
      else if (/^(high|mid|medium|low)[\s_-]?back$/i.test(t)) out.push(titleCase(t.replace(/[_-]/g, ' ')));
    });
    if (out.length) return uniq(out);
    var text = String(p.title || '') + ' ' + String(p.product_type || '');
    if (/\bhigh[\s-]?back\b/i.test(text)) out.push('High Back');
    if (/\b(mid|medium|middle)[\s-]?back\b/i.test(text)) out.push('Mid Back');
    if (/\blow[\s-]?back\b/i.test(text)) out.push('Low Back');
    return out;
  }

  function loadIndex(collectionUrl) {
    if (productIndexPromise) return productIndexPromise;
    var index = {};
    function page(n) {
      return fetch(collectionUrl + '/products.json?limit=250&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('products.json ' + r.status); return r.json(); })
        .then(function (data) {
          var products = (data && data.products) || [];
          products.forEach(function (p) {
            index[p.handle] = {
              category: p.product_type ? [titleCase(p.product_type)] : [],
              color: colorsFor(p),
              back: backFor(p)
            };
          });
          if (products.length === 250 && n < 20) return page(n + 1);
          return index;
        });
    }
    productIndexPromise = page(1);
    return productIndexPromise;
  }

  // ---------- state <-> URL ----------
  function readState() {
    var params = new URLSearchParams(window.location.search);
    ORDER.forEach(function (k) {
      var raw = params.get(PARAM[k]);
      state[k] = raw ? raw.split('|').filter(Boolean) : [];
    });
  }

  function writeState(replace) {
    var url = new URL(window.location.href);
    ORDER.forEach(function (k) {
      if (state[k].length) url.searchParams.set(PARAM[k], state[k].join('|'));
      else url.searchParams.delete(PARAM[k]);
    });
    url.searchParams.delete('section_id');
    var fn = replace ? 'replaceState' : 'pushState';
    window.history[fn](Object.assign({}, window.history.state || {}, { sfg: true }), '', url.toString());
  }

  function anyActive() {
    return ORDER.some(function (k) { return state[k].length > 0; });
  }

  function matches(attrs) {
    if (!attrs) return false;
    return ORDER.every(function (k) {
      if (!state[k].length) return true;
      var have = attrs[k].map(function (v) { return v.toLowerCase(); });
      return state[k].some(function (v) { return have.indexOf(v.toLowerCase()) !== -1; });
    });
  }

  // ---------- UI ----------
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function swatchCss(name) {
    var key = name.toLowerCase().replace(/\s+/g, '');
    return CSS_COLOR[key] || key;
  }

  function buildGroups(mount, index) {
    var enabled = {
      category: mount.dataset.nativeCategory !== 'true',
      color: mount.dataset.nativeColor !== 'true',
      back: mount.dataset.nativeBack !== 'true' && mount.dataset.backAllowed === 'true'
    };
    var counts = { category: {}, color: {}, back: {} };
    Object.keys(index).forEach(function (h) {
      ORDER.forEach(function (k) {
        index[h][k].forEach(function (v) { counts[k][v] = (counts[k][v] || 0) + 1; });
      });
    });

    var html = '';
    ORDER.forEach(function (k) {
      if (!enabled[k]) return;
      var values = Object.keys(counts[k]).sort(function (a, b) { return a.localeCompare(b); });
      if (!values.length) return;
      var showCounts = mount.dataset.showCounts === 'true';
      html += '<div class="sfg-sidebar__section sfg-smart-group" data-kind="' + k + '">' +
        '<details class="sfg-group sfg-group--' + k + '" open>' +
        '<summary class="sfg-group__summary"><h3 class="sfg-sidebar__title">' + LABEL[k] + '</h3>' +
        '<span class="sfg-group__icon" aria-hidden="true"></span></summary>' +
        '<div class="sfg-group__body"><ul class="sfg-checkbox-list" role="list">';
      values.forEach(function (v, i) {
        var id = 'SfgSmart-' + mount.dataset.sectionId + '-' + k + '-' + i;
        var checked = state[k].some(function (s) { return s.toLowerCase() === v.toLowerCase(); });
        html += '<li><label class="sfg-checkbox-label" for="' + id + '">' +
          '<input type="checkbox" id="' + id + '" data-sfg-smart="' + k + '" value="' + esc(v) + '"' + (checked ? ' checked' : '') + '>' +
          (k === 'color' ? '<span class="sfg-swatch" style="background:' + esc(swatchCss(v)) + '" aria-hidden="true"></span>' : '') +
          '<span>' + esc(v) + '</span>' +
          (showCounts ? '<span class="sfg-checkbox-label__count">(' + counts[k][v] + ')</span>' : '') +
          '</label></li>';
      });
      html += '</ul></div></details></div>';
    });
    mount.innerHTML = html;
  }

  function renderPills(root) {
    var host = root.querySelector('[data-sfg-smart-pills]');
    if (!host) return;
    if (!anyActive()) { host.innerHTML = ''; host.hidden = true; return; }
    var html = '';
    ORDER.forEach(function (k) {
      state[k].forEach(function (v) {
        html += '<button type="button" class="sfg-pill" data-sfg-smart-remove="' + k + '" data-value="' + esc(v) + '">' +
          '<span>' + LABEL[k] + ': ' + esc(v) + '</span><span class="sfg-pill__x" aria-hidden="true">&times;</span></button>';
      });
    });
    html += '<button type="button" class="sfg-clear-btn" data-sfg-smart-clear>Clear</button>';
    host.innerHTML = html;
    host.hidden = false;
  }

  // ---------- grid ----------
  function fetchAllCards(root) {
    var sectionId = root.dataset.sectionId;
    var results = root.querySelector('.sfg-results');
    var pages = Math.min(parseInt(results && results.dataset.pages, 10) || 1, MAX_PAGES);
    var base = new URL(window.location.href);
    ORDER.forEach(function (k) { base.searchParams.delete(PARAM[k]); });
    base.searchParams.delete('page');
    var key = base.toString();
    if (gridCache[key]) return Promise.resolve(gridCache[key]);

    var requests = [];
    for (var n = 1; n <= pages; n++) {
      var u = new URL(key);
      u.searchParams.set('page', n);
      u.searchParams.set('section_id', sectionId);
      requests.push(fetch(u.toString(), { credentials: 'same-origin' }).then(function (r) { return r.text(); }));
    }
    return Promise.all(requests).then(function (htmls) {
      var cards = [];
      var seen = {};
      htmls.forEach(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('#SfgProductGrid-' + sectionId + ' > .sfg-grid__item').forEach(function (li) {
          var h = li.getAttribute('data-handle');
          if (h && !seen[h]) { seen[h] = true; cards.push(li); }
        });
      });
      gridCache[key] = cards;
      return cards;
    });
  }

  function applyToGrid(root, index) {
    var grid = root.querySelector('.sfg-grid');
    var results = root.querySelector('.sfg-results');
    if (!results) return Promise.resolve();
    var pagination = results.querySelector('.sfg-pagination');
    var countLine = results.querySelector('.sfg-pagination__count');
    var toolbarCount = root.querySelector('.sfg-count');

    if (!grid && !anyActive()) return Promise.resolve();

    if (!anyActive()) {
      // Restore the server-rendered page.
      if (root._sfgOriginal) {
        results.innerHTML = root._sfgOriginal;
        root._sfgOriginal = null;
      }
      return Promise.resolve();
    }

    if (!root._sfgOriginal) root._sfgOriginal = results.innerHTML;
    root.setAttribute('data-loading', 'true');

    return fetchAllCards(root).then(function (cards) {
      var matched = cards.filter(function (li) { return matches(index[li.getAttribute('data-handle')]); });
      var gridClass = (grid && grid.className) || 'sfg-grid';
      var gridId = 'SfgProductGrid-' + root.dataset.sectionId;
      if (!matched.length) {
        results.innerHTML = '<div class="sfg-empty"><p>No products match these filters.</p>' +
          '<p><button type="button" class="sfg-clear-btn" data-sfg-smart-clear>Clear filters</button></p></div>';
      } else {
        var ul = document.createElement('ul');
        ul.className = gridClass;
        ul.id = gridId;
        ul.setAttribute('role', 'list');
        matched.forEach(function (li) { ul.appendChild(document.importNode(li, true)); });
        results.innerHTML = '';
        results.appendChild(ul);
        var p = document.createElement('p');
        p.className = 'sfg-pagination__count';
        p.textContent = 'Showing ' + matched.length + ' of ' + cards.length + ' products';
        results.appendChild(p);
      }
      if (toolbarCount) toolbarCount.textContent = matched.length + (matched.length === 1 ? ' product' : ' products');
      root.removeAttribute('data-loading');
      document.dispatchEvent(new CustomEvent('sfg:smart-rendered', { detail: { sectionId: root.dataset.sectionId } }));
    }).catch(function (e) {
      console.error('Smart filters:', e);
      root.removeAttribute('data-loading');
    });
  }

  // ---------- wiring ----------
  function init(root) {
    var mount = root.querySelector('[data-sfg-smart-mount]');
    if (!mount) return;
    root._sfgOriginal = null;
    readState();
    loadIndex(mount.dataset.collectionUrl).then(function (index) {
      buildGroups(mount, index);
      renderPills(root);
      if (anyActive()) applyToGrid(root, index);
    }).catch(function (e) {
      console.error('Smart filters:', e);
    });
  }

  function onChange(root, kind, value, on) {
    var list = state[kind].filter(function (v) { return v.toLowerCase() !== value.toLowerCase(); });
    if (on) list.push(value);
    state[kind] = list;
    writeState(false);
    renderPills(root);
    // keep sidebar checkboxes in sync (pill removals)
    root.querySelectorAll('[data-sfg-smart="' + kind + '"]').forEach(function (cb) {
      if (cb.value.toLowerCase() === value.toLowerCase()) cb.checked = on;
    });
    productIndexPromise.then(function (index) { applyToGrid(root, index); });
  }

  function bind(root) {
    if (root._sfgSmartBound) return;
    root._sfgSmartBound = true;

    root.addEventListener('change', function (e) {
      var cb = e.target.closest('[data-sfg-smart]');
      if (!cb) return;
      e.stopPropagation();
      onChange(root, cb.dataset.sfgSmart, cb.value, cb.checked);
    });

    root.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-sfg-smart-remove]');
      if (rm) { onChange(root, rm.dataset.sfgSmartRemove, rm.dataset.value, false); return; }
      if (e.target.closest('[data-sfg-smart-clear]')) {
        ORDER.forEach(function (k) { state[k] = []; });
        writeState(false);
        root.querySelectorAll('[data-sfg-smart]').forEach(function (cb) { cb.checked = false; });
        renderPills(root);
        productIndexPromise.then(function (index) { applyToGrid(root, index); });
      }
    });
  }

  function start() {
    document.querySelectorAll('.sfg-wrap').forEach(function (root) {
      bind(root);
      init(root);
    });
  }

  // The collection section swaps its markup in place when native filters,
  // sorting or pagination change; rebuild on top of the new markup and put
  // our params back into the URL it just pushed.
  document.addEventListener('sfg:rendered', function (e) {
    var root = document.getElementById('SidebarFilterGrid-' + (e.detail && e.detail.sectionId));
    if (!root) return;
    var saved = { category: state.category.slice(), color: state.color.slice(), back: state.back.slice() };
    gridCache = {};
    state = saved;
    if (anyActive()) writeState(true);
    init(root);
  });

  window.addEventListener('popstate', function () {
    document.querySelectorAll('.sfg-wrap').forEach(function (root) { init(root); });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
